import { callButton, el, icon } from './dom.js';
import { infoButton } from './info.js';
import { knock } from './secretTaps.js';

/**
 * The settings sheet: what a player may want to change, and, apart from it,
 * the tools the project tests with.
 *
 * The two never mix. Game settings are a short card of rows a player
 * understands at a glance. The developer tools sit behind a striped service
 * panel, folded away until opened: replays, an open building, coins, a fresh
 * save. They are for playtests, so a player never sees them (ADR-0048): five
 * quick taps on the sheet's title show them, and a save that has used them
 * keeps showing them, so an open building can always be closed again.
 */

export interface SettingsView {
  readonly sound: boolean;
  /** The music (ADR-0056), apart from the sounds. */
  readonly music: boolean;
  /** Taps queue as stops in order (ADR-0052). */
  readonly tapAhead: boolean;
  readonly unlockAll: boolean;
  /** The save has used the developer tools (it is marked, and its runs say so). */
  readonly devTools: boolean;
  readonly coins: number;
  /** Runs kept on this phone. */
  readonly replays: number;
  readonly stamp: string;
}

export interface SettingsActions {
  /** What the sheet shows, read each time it opens. */
  readonly view: () => SettingsView;
  readonly sound: (on: boolean) => void;
  readonly music: (on: boolean) => void;
  readonly tapAhead: (on: boolean) => void;
  readonly guideAgain: () => void;
  /** Sends and downloads the recorded runs; resolves to a line saying what happened. */
  readonly sendReplays: () => Promise<string>;
  readonly unlockAll: (on: boolean) => void;
  readonly addCoins: () => void;
  readonly reset: () => void;
}

export interface Settings {
  readonly sheet: HTMLElement;
  open(): void;
  close(): void;
}

/** One row: an icon, what it is, a line about it, and its control. */
function row(
  iconName: string,
  title: string,
  line: HTMLElement,
  control: HTMLElement,
): HTMLElement {
  return el('div', { class: 'setting' }, [
    el('span', { class: 'setting-icon' }, [icon(iconName)]),
    el('div', { class: 'setting-text' }, [
      el('div', { class: 'setting-title', text: title }),
      line,
    ]),
    control,
  ]);
}

/** An on/off switch: a slider, read by a screen reader as one. */
function switchFor(label: string, onChange: (on: boolean) => void): HTMLButtonElement {
  const button = el('button', {
    class: 'switch',
    type: 'button',
    role: 'switch',
    'aria-label': label,
    'aria-checked': 'false',
  });
  button.addEventListener('click', () => {
    const on = button.getAttribute('aria-checked') !== 'true';
    button.setAttribute('aria-checked', String(on));
    onChange(on);
  });
  return button;
}

/**
 * Sound and music: a big round switch each, lit when on (icon-first since
 * PLAYTEST_LOG 2026-10-29). Read by a screen reader as a switch.
 */
function bigToggle(
  iconName: string,
  label: string,
  onChange: (on: boolean) => void,
): HTMLButtonElement {
  const button = el(
    'button',
    {
      class: 'big-toggle',
      type: 'button',
      role: 'switch',
      'aria-label': label,
      'aria-checked': 'false',
    },
    [
      el('span', { class: 'big-toggle-disc' }, [icon(iconName)]),
      el('span', { class: 'big-toggle-label', text: label }),
      el('span', { class: 'big-toggle-state', text: 'Off' }),
    ],
  );
  button.addEventListener('click', () => {
    const on = button.getAttribute('aria-checked') !== 'true';
    setOn(button, on);
    onChange(on);
  });
  return button;
}

function setOn(button: HTMLElement, on: boolean): void {
  button.setAttribute('aria-checked', String(on));
  const state = button.querySelector('.big-toggle-state');
  if (state) state.textContent = on ? 'On' : 'Off';
}

/** A game setting in one line: its icon, its name, its "i", its control. */
function shortRow(
  iconName: string,
  title: string,
  about: string,
  control: HTMLElement,
): HTMLElement {
  return el('div', { class: 'setting setting-short' }, [
    el('span', { class: 'setting-icon' }, [icon(iconName)]),
    el('div', { class: 'setting-title', text: title }),
    infoButton(title, [about]),
    control,
  ]);
}

function pill(text: string, tone?: 'danger'): HTMLButtonElement {
  const button = el('button', { class: 'brass-pill setting-pill', type: 'button' }, [
    el('span', { text }),
  ]);
  if (tone) button.dataset.tone = tone;
  return button;
}

const line = (text = ''): HTMLElement => el('div', { class: 'setting-line', text });

export function buildSettings(actions: SettingsActions): Settings {
  // ---- Game: what a player would want to change.
  const sound = bigToggle('sound', 'Sound', actions.sound);
  const music = bigToggle('music', 'Music', actions.music);
  const tapAhead = switchFor('Tap ahead', actions.tapAhead);
  const guide = pill('Replay');
  guide.addEventListener('click', () => {
    actions.guideAgain();
    (guide.firstChild as HTMLElement).textContent = 'Done';
    guide.disabled = true;
  });

  const game = el('section', { class: 'settings-section' }, [
    el('div', { class: 'big-toggles' }, [sound, music]),
    el('div', { class: 'settings-card' }, [
      shortRow(
        'queue',
        'Tap ahead',
        'Tap up to three floors: the lift goes to them in turn. Off: it goes to the last floor you tap.',
        tapAhead,
      ),
      shortRow(
        'guide',
        'Guided shifts',
        'Shifts 1–3 show you how to play again, the next time you play them.',
        guide,
      ),
    ]),
  ]);

  // ---- Developer tools: what the project tests with, folded away.
  const send = pill('Send');
  const sendLine = line();
  send.addEventListener('click', () => {
    send.disabled = true;
    sendLine.textContent = 'Sending…';
    void actions.sendReplays().then((said) => {
      sendLine.textContent = said;
      send.disabled = false;
    });
  });

  const unlock = switchFor('Unlock every level', actions.unlockAll);

  const coins = pill('+500');
  const coinsLine = line();
  coins.addEventListener('click', () => {
    actions.addCoins();
    coinsLine.textContent = `${actions.view().coins} coins now.`;
  });

  // Two taps, as leaving a shift is: the first arms it, the second erases.
  const reset = pill('Reset', 'danger');
  const resetLine = line();
  let armed: ReturnType<typeof setTimeout> | null = null;
  const disarm = () => {
    if (armed !== null) clearTimeout(armed);
    armed = null;
    reset.dataset.armed = 'false';
    (reset.firstChild as HTMLElement).textContent = 'Reset';
    resetLine.textContent = 'Back to a fresh save: stars, coins, parts and lessons.';
  };
  reset.addEventListener('click', () => {
    if (armed === null) {
      reset.dataset.armed = 'true';
      (reset.firstChild as HTMLElement).textContent = 'Sure?';
      resetLine.textContent = 'Tap again to erase everything on this phone.';
      armed = setTimeout(disarm, 4000);
      return;
    }
    disarm();
    actions.reset();
  });

  const stamp = el('div', { class: 'devtools-stamp' });
  const tools = el('details', { class: 'devtools', hidden: true }, [
    el('summary', {}, [
      el('span', { class: 'setting-icon' }, [icon('wrench')]),
      el('div', { class: 'setting-text' }, [
        el('div', { class: 'setting-title', text: 'Developer tools' }),
        line('For playtests, not for players.'),
      ]),
      el('span', { class: 'devtools-chevron' }, [icon('chevron')]),
    ]),
    el('div', { class: 'devtools-body' }, [
      row('upload', 'Recorded runs', sendLine, send),
      row('unlock', 'Unlock every level', line('Open every shift, to test any of them.'), unlock),
      row('coin', 'Add coins', coinsLine, coins),
      row('retry', 'Reset progress', resetLine, reset),
      stamp,
    ]),
  ]);

  const done = el('button', { class: 'action-pill settings-done', type: 'button' }, ['Done']);
  const head = el('header', { class: 'settings-head lintel' }, [
    el('span', { class: 'settings-mark' }, [icon('cog')]),
    el('h2', { text: 'Settings' }),
  ]);
  // Shown for the rest of this visit once knocked for; a reload hides them again.
  let knocked = false;
  const knocking = knock(5, 1500);
  head.addEventListener('click', (event) => {
    if ((event.target as HTMLElement).closest('button') || !knocking(event.timeStamp)) return;
    knocked = true;
    tools.hidden = false;
    tools.open = true;
    tools.scrollIntoView({ block: 'nearest' });
  });
  const body = el('div', { class: 'settings', role: 'dialog', 'aria-label': 'Settings' }, [
    head,
    el('div', { class: 'settings-scroll' }, [game, tools, done]),
  ]);
  const sheet = el('div', { class: 'sheet', 'data-open': 'false' }, [body]);

  const settings: Settings = {
    sheet,
    open() {
      const view = actions.view();
      setOn(sound, view.sound);
      setOn(music, view.music);
      tapAhead.setAttribute('aria-checked', String(view.tapAhead));
      unlock.setAttribute('aria-checked', String(view.unlockAll));
      tools.hidden = !(knocked || view.devTools);
      sendLine.textContent = `${view.replays} ${view.replays === 1 ? 'run' : 'runs'} on this phone. Sends them in and downloads a copy.`;
      coinsLine.textContent = `${view.coins} coins now. For testing the garage.`;
      stamp.textContent = `build ${view.stamp}`;
      guide.disabled = false;
      (guide.firstChild as HTMLElement).textContent = 'Replay';
      disarm();
      sheet.dataset.open = 'true';
    },
    close() {
      disarm();
      sheet.dataset.open = 'false';
    },
  };
  head.append(callButton('close', 'Close settings', () => settings.close()));
  done.addEventListener('click', () => settings.close());
  sheet.addEventListener('click', (event) => {
    if (event.target === sheet) settings.close();
  });
  return settings;
}
